export function CardShell({ span = "", children, className = "" }) {
  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border border-white/[0.09] p-8 sm:p-10 shadow-[0_1px_0_hsl(0_0%_100%/0.04)_inset,0_20px_50px_-30px_rgb(0_0_0/0.6)] ring-1 ring-white/[0.02] transition-all duration-500 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[0_1px_0_hsl(0_0%_100%/0.06)_inset,0_25px_60px_-30px_hsl(var(--primary)/0.35)] ${span} ${className}`}
      style={{
        backgroundColor: "hsl(240 8% 11%)",
        backgroundImage: [
          // Carbon-fiber weave: two offset half-cells running in opposite diagonals
          "linear-gradient(135deg, rgba(255,255,255,0.025) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.025) 50%, rgba(255,255,255,0.025) 75%, transparent 75%, transparent)",
          "linear-gradient(45deg, rgba(255,255,255,0.02) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.02) 75%, transparent 75%, transparent)",
        ].join(","),
        backgroundSize: "6px 6px, 6px 6px",
      }}
      data-testid="feature-card-shell"
    >
      {/* Livery top-stripe — subtle racing accent that visually anchors the card edge */}
      <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-primary/70 via-primary/30 to-transparent" />
      {/* Racing chevron corner accent — organic motorsport motif */}
      <svg
        aria-hidden
        viewBox="0 0 200 200"
        className="pointer-events-none absolute -bottom-10 -right-10 h-44 w-44 text-primary/[0.05] transition-all duration-700 group-hover:text-primary/[0.14] group-hover:-translate-x-1.5"
      >
        <path d="M40 180 L140 40 M70 180 L170 40 M100 180 L200 40" stroke="currentColor" strokeWidth="10" fill="none" strokeLinecap="round" />
      </svg>
      {/* Speed lines fade in on hover */}
      <div className="pointer-events-none absolute inset-y-0 right-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100">
        <div className="absolute right-0 top-16 h-px w-20 bg-gradient-to-l from-primary/60 to-transparent" />
        <div className="absolute right-0 top-24 h-px w-12 bg-gradient-to-l from-primary/40 to-transparent" />
        <div className="absolute right-0 top-32 h-px w-28 bg-gradient-to-l from-primary/50 to-transparent" />
      </div>
      {/* Soft red glow top-left */}
      <div aria-hidden className="pointer-events-none absolute -left-16 -top-16 h-40 w-40 rounded-full bg-primary/5 blur-3xl" />
      <div className="relative">{children}</div>
    </div>
  );
}

export function FeatureTitle({ eyebrow, pos, children, size = "md" }) {
  const titleSize = size === "sm" ? "text-xl sm:text-2xl" : "text-2xl sm:text-[28px]";
  return (
    <div className="relative">
      {(eyebrow || pos) && (
        <div className="mb-2 flex items-center gap-2">
          {pos && (
            <span className="flex h-6 items-center justify-center rounded-[5px] bg-primary px-1.5 font-mono text-[11px] font-bold tabular-nums text-primary-foreground shadow-[0_0_12px_-2px_hsl(var(--primary)/0.7)]">
              {pos}
            </span>
          )}
          {eyebrow && (
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-primary/85">
              {eyebrow}
            </p>
          )}
        </div>
      )}
      <h3 className={`font-display font-bold uppercase leading-[1.05] ${titleSize}`}>
        {children}
      </h3>
      <span aria-hidden className="mt-3 block h-px w-20 bg-gradient-to-r from-primary via-primary/50 to-transparent" />
    </div>
  );
}

/**
 * Timing-tower building blocks — the "Concept 1" leaderboard look.
 * SectorBar mimics F1 mini-sector lights (green / purple segments).
 */
const SECTOR_PATTERNS = [
  ["g", "p", "g"], ["p", "g", "g"], ["g", "g", "p"],
  ["p", "p", "g"], ["g", "p", "p"], ["p", "g", "p"],
];

export function SectorBar({ i = 0 }) {
  const seg = SECTOR_PATTERNS[i % SECTOR_PATTERNS.length];
  const col = (c) => (c === "g" ? "bg-emerald-400/80" : "bg-violet-400/80");
  return (
    <span className="flex flex-shrink-0 items-center gap-0.5" aria-hidden>
      {seg.map((c, k) => (
        <span key={k} className={`h-1.5 w-4 rounded-[1px] ${col(c)} shadow-[0_0_6px_-1px] ${c === "g" ? "shadow-emerald-400/50" : "shadow-violet-400/50"}`} />
      ))}
    </span>
  );
}

export function TimingRow({ n, label, sub, trailing, highlight = false }) {
  return (
    <li
      className={`flex items-center gap-3 rounded-md border px-3 py-2.5 transition-colors duration-300 ${
        highlight
          ? "border-primary/50 bg-primary/[0.07]"
          : "border-white/15 bg-background/50 hover:border-primary/40"
      }`}
    >
      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[5px] bg-primary font-mono text-[11px] font-bold tabular-nums text-primary-foreground">
        {String(n).padStart(2, "0")}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold uppercase tracking-wide text-foreground/90">{label}</span>
        {sub && <span className="mt-0.5 block text-xs normal-case tracking-normal text-muted-foreground/70">{sub}</span>}
      </span>
      <span className="flex-shrink-0">{trailing != null ? trailing : <SectorBar i={n - 1} />}</span>
    </li>
  );
}

export function TimingList({ items, className = "" }) {
  return (
    <ol className={`space-y-2 ${className}`}>
      {items.map((it, i) => {
        const obj = typeof it === "string" ? { label: it } : it;
        return <TimingRow key={obj.label || i} n={i + 1} {...obj} />;
      })}
    </ol>
  );
}
